import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { useCommunicationSecretConsentStatus } from '../../hooks/api';
import { sanitizeReturnTo } from '../../lib/safePath';

/**
 * 通信の秘密同意ゲート。
 *
 * `AuthGate` の内側・`ProfileGate` の外側に挿入し、認証済み LINE ユーザーが
 * 現行 `version_major` と一致する取得済み行を持たない場合は `/consent` へ強制遷移する
 * （設計書 §4.2 / §5-6）。
 *
 * `AppReadyGate` の他ゲートと同一の `LoadingSpinner fullPage` を返すことで、
 * 段階的ローディングを 1 枚のスピナーに連結し点滅を防ぐ（AppReadyGate と同型）。
 *
 * fetch 失敗（ネットワーク・BE 5xx・タイムアウト）と「未取得確定 (`has_current: false`)」を
 * 区別する（PR-3 レビュー指摘）。前者を全ユーザー /consent 強制リダイレクトにすると、
 * 本 EP の一時障害が「同意画面への誘導」ではなく「アプリ全体の機能停止」に直結する。
 * fetch 失敗時は ErrorMessage + refetch 導線で表示し、確定的な `has_current === false` の
 * 場合のみ強制遷移する。
 */
export const CommunicationConsentGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const { data, isLoading, isError, refetch } = useCommunicationSecretConsentStatus();

  if (isLoading) {
    return <LoadingSpinner fullPage message="読み込み中..." />;
  }

  // fetch 失敗 or レスポンス欠落は「未取得確定」ではなく「判定不能」として扱い、
  // ユーザーにリトライ導線を提示する。全画面が本 EP の可用性に依存するため、
  // 一時的な障害を同意画面への強制遷移に変換しない。
  if (isError || !data) {
    return (
      <ErrorMessage
        fullPage
        title="一時的にご利用できません"
        description="通信の秘密同意ステータスの取得に失敗しました。しばらく待ってから再度お試しください。"
        onRetry={() => refetch()}
        retryLabel="再読み込み"
      />
    );
  }

  if (data.has_current !== true) {
    // location.pathname は React Router 経由の絶対パスだが、多重防御として
    // sanitizeReturnTo を通し、`//` protocol-relative URL / 外部パスを弾く
    // （PR-3 レビュー指摘 / safePolicyUrl と同じガード思想）。
    const returnTo = sanitizeReturnTo(location.pathname + location.search);
    // Gate 経路であることを明示するため isGate:true を積む。
    // ConsentPage 側は state.returnTo の有無ではなく isGate で判定する
    // （onboarding 経路と Gate 経路の混同を防ぐ / PR-3 レビュー第 4 巡 critical 指摘）。
    return (
      <Navigate
        to="/consent"
        state={{ isGate: true, returnTo }}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default CommunicationConsentGate;
